import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Car, Droplets, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API, authFetch } from '../utils/api';

const LOCATIONS = {
  'osf': 'OSF',
  'civic_center': 'Civic Center',
  'civic_center_west': 'Civic Center West',
  'great_park': 'Great Park'
};

const WASH_TYPES = {
  'wash_only_doors_locked': 'Wash Only Doors Locked',
  'interior_and_wash': 'Interior & Wash',
  'wash_only': 'Wash Only',
  'interior_only': 'Interior Only',
  'not_done': 'Not Done'
};

export default function Dashboard({ user }) {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [location.key]);

  const fetchData = async () => {
    try {
      const [analyticsRes, historyRes, vehiclesRes] = await Promise.all([
        authFetch(`${API}/analytics/dashboard`),
        authFetch(`${API}/history`),
        authFetch(`${API}/vehicles`)
      ]);

      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
      if (historyRes.ok) {
        setHistory(await historyRes.json());
      }
      if (vehiclesRes.ok) {
        setVehicles(await vehiclesRes.json());
      }
    } catch (error) {
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.vehicle_id === vehicleId);
    if (!vehicle) return { display: vehicleId, details: '' };
    return {
      display: vehicle.unit_number || '-',
      details: `${vehicle.make || ''} ${vehicle.model || ''}`.trim()
    };
  };

  const getLocationLabel = (value) => LOCATIONS[value] || value || '-';
  const getWashTypeLabel = (value) => WASH_TYPES[value] || value || '-';

  const handleExport = async () => {
    try {
      const response = await authFetch(`${API}/export/dashboard`, {
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dashboard_report.xlsx';
      a.click();
      toast.success('Dashboard exported successfully');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-full">
          <p className="text-slate-500">Loading...</p>
        </div>
      </Layout>
    );
  }

  const stats = [
    { name: 'Fleet Size', value: analytics?.total_vehicles || 0, icon: Car, color: 'text-sky-500' },
    { name: 'Total Washes', value: analytics?.total_washes || 0, icon: Droplets, color: 'text-blue-500' },
  ];

  const recentWashes = history;
  const isAdmin = user?.role === 'administrator' || user?.role === 'admin';

  return (
    <Layout user={user}>
      <div className="p-4 lg:p-12 space-y-6 lg:space-y-8 bg-slate-200 min-h-screen">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-slate-600 text-sm lg:text-base mt-1 lg:mt-2">Welcome back, {user?.name}</p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={handleExport} data-testid="export-dashboard-button" className="text-xs lg:text-sm">
              <Download className="h-4 w-4 mr-1 lg:mr-2" />
              EXPORT
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 lg:gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border border-slate-200 bg-white" data-testid={`stat-card-${index}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                    {stat.name}
                  </CardTitle>
                  <Icon className={`h-5 w-5 ${stat.color}`} strokeWidth={1.5} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-heading font-bold tracking-tight">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border border-slate-200 bg-white">
          <CardHeader>
            <CardTitle className="font-heading text-xl uppercase tracking-tight">Recent Washes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentWashes.length > 0 ? (
                recentWashes.map((wash, index) => {
                  const vehicleInfo = getVehicleInfo(wash.vehicle_id);
                  return (
                    <div key={wash.history_id || index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-testid={`recent-wash-${index}`}>
                      <div>
                        <p className="font-semibold font-mono text-sm">{vehicleInfo.display}</p>
                        <p className="text-sm text-slate-500">{vehicleInfo.details}</p>
                      </div>
                      <div className="text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                          wash.wash_type === 'not_done' 
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : wash.wash_type === 'interior_and_wash'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-slate-50 text-slate-700 border-slate-200'
                        }`}>
                          {getWashTypeLabel(wash.wash_type)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          {getLocationLabel(wash.location)}
                        </span>
                        <p className="text-xs text-slate-500 mt-1">{new Date(wash.wash_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-slate-500 text-center py-8">No recent washes</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
